import { safeParseInt } from "@/utilities";
import { Spinner } from "@heroui/react";
import { getBucketObjectURL, safeParseDate } from "@sr-partner-portal/common";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Api } from "./api";
import { config } from "./config";
import { useSessionStorage } from "./hooks";
import type { JobExperience, LanguageSkill, Resume, Training } from "./types";

const resumeContentStorageKey = "resumeContent";

interface ResumeContent {
    step1: Resume | null;
    step2: {
        experienceData: JobExperience[];
        trainingData: Training[];
        softwareSkills: string[];
        drivingLicenses: string[];
        additionalSkills: string[];
        hobbies: string[];
        languages: LanguageSkill[];
    };
    step3: {
        photo: string | null;
        color: string;
    };
}

interface ResumeBuilderContextType {
    resumeContent: ResumeContent;
    updateStep1: (data: Resume) => void;
    updateStep2: ({
        experienceData,
        trainingData,
        softwareSkills,
        drivingLicenses,
        additionalSkills,
        hobbies,
        languages,
    }: {
        experienceData?: JobExperience[];
        trainingData?: Training[];
        softwareSkills?: string[];
        drivingLicenses?: string[];
        additionalSkills?: string[];
        hobbies?: string[];
        languages?: LanguageSkill[];
    }) => void;
    updateStep3: (photo: string | null, color: string) => void;
    design: {
        resumePrimaryColor: string;
        resumePrimaryContrastColor: string;
    };
    privacy: string | null;
    imprint: string | null;
    token: string | null;
    logoUrl?: string | null;
    initialApplicantEmail?: string;
}

const ResumeBuilderContext = createContext<
    ResumeBuilderContextType | undefined
>(undefined);

const parseResumeContent = (value: string): ResumeContent => {
    const parsedValue = JSON.parse(value);

    return {
        ...parsedValue,
        step1: {
            ...parsedValue.step1,
            birthDate: safeParseDate(parsedValue.step1?.birthDate),
            numberOfChildren: safeParseInt(parsedValue.step1?.numberOfChildren),
        },
        step2: {
            ...parsedValue.step2,
            experienceData: parsedValue.step2.experienceData.map((item) => ({
                ...item,
                startDate: safeParseDate(item.startDate),
                endDate: safeParseDate(item.endDate),
            })),
            trainingData: parsedValue.step2.trainingData?.map((item) => ({
                ...item,
                startDate: safeParseDate(item.startDate),
                endDate: safeParseDate(item.endDate),
            })),
        },
    };
};

const ResumeBuilderProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    // get query params from url
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    // don't save photo in session storage because of quota (limited storage)
    const [photo, setPhoto] = useState(null);
    const [resumeContent, setResumeContent] = useSessionStorage<ResumeContent>(
        token ? `${resumeContentStorageKey}_${token}` : resumeContentStorageKey,
        {
            step1: null,
            step2: {
                experienceData: [],
                trainingData: [],
                softwareSkills: [],
                drivingLicenses: [],
                additionalSkills: [],
                hobbies: [],
                languages: [],
            },
            step3: {
                photo: null,
                color: config.defaultResumePdfColor,
            },
        },
        parseResumeContent,
    );
    const [initialApplicantEmail, setInitialApplicantEmail] = useState<
        string | null
    >(null);
    const [privacy, setPrivacy] = useState<string | null>(null);
    const [imprint, setImprint] = useState<string | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [design, setDesign] = useState<{
        resumePrimaryColor: string;
        resumePrimaryContrastColor: string;
    } | null>(null);

    const updateStep1 = (data: Resume) => {
        setResumeContent((prev) => ({
            ...prev,
            step1: data,
        }));
    };

    const updateStep2 = ({
        experienceData,
        trainingData,
        softwareSkills,
        drivingLicenses,
        additionalSkills,
        hobbies,
        languages,
    }: {
        experienceData?: JobExperience[];
        trainingData?: Training[];
        softwareSkills?: string[];
        drivingLicenses?: string[];
        additionalSkills?: string[];
        hobbies?: string[];
        languages?: LanguageSkill[];
    }) => {
        setResumeContent((prev) => ({
            ...prev,
            step2: {
                ...prev.step2,
                ...(experienceData ? { experienceData } : {}),
                ...(trainingData ? { trainingData } : {}),
                ...(softwareSkills ? { softwareSkills } : {}),
                ...(drivingLicenses ? { drivingLicenses } : {}),
                ...(additionalSkills ? { additionalSkills } : {}),
                ...(hobbies ? { hobbies } : {}),
                ...(languages ? { languages } : {}),
            },
        }));
    };

    const updateStep3 = (photo: string | null, color: string) => {
        setResumeContent((prev) => ({
            ...prev,
            step3: {
                photo: null,
                color,
            },
        }));
        setPhoto(photo);
    };

    const resumeDataQuery = useQuery({
        queryKey: ["resume-data", token],
        queryFn: async () => Api.getResumeData(token),
        enabled: !!token
    });

    useEffect(() => {
        if (!resumeDataQuery.data) {
            return;
        }

        setResumeContent((prev) => ({
            ...prev,
            step1: {
                ...prev.step1,
                name:
                    prev.step1?.name ||
                    resumeDataQuery.data.applicant.name ||
                    "",
                email:
                    prev.step1?.email ||
                    resumeDataQuery.data.applicant.emailAddress ||
                    "",
                phone:
                    prev.step1?.phone ||
                    resumeDataQuery.data.applicant.phoneNumber ||
                    "",
            },
        }));
        setDesign({
            resumePrimaryColor:
                resumeDataQuery.data.company.resumePrimaryColor ??
                config.defaultResumePrimaryColor,
            resumePrimaryContrastColor:
                resumeDataQuery.data.company.resumePrimaryContrastColor ??
                config.defaultResumePrimaryContrastColor,
        });
        setInitialApplicantEmail(resumeDataQuery.data.applicant.emailAddress);
        setPrivacy(
            resumeDataQuery.data.company.privacy ?? config.defaultPrivacy,
        );
        setImprint(
            resumeDataQuery.data.company.imprint ?? config.defaultImprint,
        );
        setLogoUrl(
            getBucketObjectURL(resumeDataQuery.data.company.logoPath) ?? null,
        );
    }, [setResumeContent, resumeDataQuery.data]);

    // Set default design colors if token is not present and therefore no data will be fetched
    useEffect(() => {
        if (token) {
            return;
        }

        setDesign({
            resumePrimaryColor: config.defaultResumePrimaryColor,
            resumePrimaryContrastColor:
                config.defaultResumePrimaryContrastColor,
        });
    }, [token]);

    if (resumeDataQuery.isError || !token) {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                <div className="flex w-full max-w-lg flex-col items-center justify-center gap-2">
                    <h1 className="font-bold text-xl">Ungültiger Link</h1>
                    <p className="text-center text-gray-600">
                        Der Link, den Sie verwendet haben, ist ungültig oder
                        abgelaufen. Bitte wenden Sie sich an Ihren
                        Ansprechpartner.
                    </p>
                </div>
            </div>
        );
    }

    if (resumeDataQuery.isLoading || !design) {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <ResumeBuilderContext.Provider
            value={{
                resumeContent: {
                    ...resumeContent,
                    step3: {
                        ...resumeContent.step3,
                        photo,
                    },
                },
                updateStep1,
                updateStep2,
                updateStep3,
                design,
                privacy,
                imprint,
                token,
                logoUrl,
                initialApplicantEmail,
            }}
        >
            {children}
        </ResumeBuilderContext.Provider>
    );
};

const useResumeBuilder = () => {
    const context = useContext(ResumeBuilderContext);
    if (context === undefined) {
        throw new Error(
            "useResumeBuilder must be used within a ResumeBuilderProvider",
        );
    }
    return context;
};

export { ResumeBuilderProvider, useResumeBuilder, type ResumeContent };
